import React from 'react';
import { UserIntegrationWithDetails } from '../../../services/api';
import { useTranslation } from '../../../contexts/TranslationContext';
import { format as formatJalali } from 'date-fns-jalali';
import { formatJsonValue } from '../../../utils/formatJsonValue';
import { toPersianDigits } from '../../../utils/validation';
import './UserIntegrationCard.css';

interface UserIntegrationCardProps {
  userIntegration: UserIntegrationWithDetails;
  onSetup: (userIntegrationId: number) => void;
  onDelete: (userIntegrationId: number) => void;
}

const UserIntegrationCard: React.FC<UserIntegrationCardProps> = ({
  userIntegration,
  onSetup,
  onDelete,
}) => {
  const { t , currentLanguage} = useTranslation();
  const { integration } = userIntegration;
  
  const isConfigured = () => {
    const config = userIntegration.configuration;
    const schema = integration.configuration_schema;
    
    if (!schema || !schema.required_fields) return true;
    
    const requiredFields = Object.keys(schema.required_fields);
    return requiredFields.every(field => config && config[field]);
  };

  const handleSetupClick = () => {
    onSetup(userIntegration.id);
  };

  const handleDeleteClick = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    onDelete(userIntegration.id);
  };

  const formatDate = (date: string | null) => {
    if (!date) return '---';
    const formatted = formatJalali(new Date(date), 'yyyy/MM/dd');
    return currentLanguage === 'fa' ? toPersianDigits(formatted) : formatted;
  };

  return (
    <div className="user-integration-card">
      <div className="integration-header">
        <div className="integration-logo-section">
          {integration.image_url ? (
            <img
              src={integration.image_url}
              alt={integration.title}
              className="integration-logo"
              onError={(e) => {
                const target = e.target as HTMLImageElement;
                target.style.display = 'none';
              }}
            />
          ) : (
            <div className="integration-logo-placeholder">
              <span>{integration.title.charAt(0).toUpperCase()}</span>
            </div>
          )}
        </div>

        <div className="integration-info">
          <h3>{integration.title}</h3>
          <p className="integration-description">{integration.brief_description}</p>
          
          <div className="integration-meta">
            <span className="added-date">{t('marketplace.myIntegrations.addedDate')} {formatDate(userIntegration.created_at)}</span>
            {userIntegration.updated_at && userIntegration.updated_at !== userIntegration.created_at && (
              <span className="updated-date">{t('marketplace.myIntegrations.updatedDate')} {formatDate(userIntegration.updated_at)}</span>
            )}
          </div>
        </div>

        <div className="integration-status-section">
          <div className="status-badges">
            <span className={`status-badge ${userIntegration.is_active ? 'active' : 'inactive'}`}>
              {userIntegration.is_active ? t('marketplace.active') : t('marketplace.inactive')}
            </span>
            
            <span className={`config-badge ${isConfigured() ? 'configured' : 'not-configured'}`}>
              {isConfigured() ? t('marketplace.myIntegrations.configured') : t('marketplace.myIntegrations.setupRequired')}
            </span>
          </div>

          <div className="pricing-info">
            <span className="price">{integration.monthly_price} {currentLanguage === 'fa' ? 'تومان' : 'IRR'}</span>
            <span className="period">{t('marketplace.monthlyPrice')}</span>
          </div>
        </div>
      </div>

      {userIntegration.notes && (
        <div className="integration-notes">
          <h4>{t('marketplace.myIntegrations.notes')}</h4>
          <p>{userIntegration.notes}</p>
        </div>
      )}

      <div className="configuration-summary">
        <h4>{t('marketplace.myIntegrations.configuration')}</h4>
        {Object.keys(userIntegration.configuration).length > 0 ? (
          <div className="config-fields">
            {Object.entries(userIntegration.configuration).map(([key, value]) => {
              const displayValue =
                typeof value === 'string' && value.includes('***')
                  ? '••••••••'
                  : formatJsonValue(value);

              return (
                <div key={key} className="config-field">
                  <span className="field-name">{key}:</span>
                  <span className="field-value" title={displayValue}>
                    {displayValue}
                  </span>
                </div>
              );
            })}
          </div>
        ) : (
          <p className="no-config">{t('marketplace.myIntegrations.noConfiguration')}</p>
        )}
      </div>

      <div className="integration-actions">
        <button
          className="btn-secondary setup-btn"
          onClick={handleSetupClick}
        >
          {isConfigured() ? t('marketplace.myIntegrations.editConfiguration') : t('marketplace.myIntegrations.setupIntegration')}
        </button>

        {integration.third_party_link && (
          <a
            href={integration.third_party_link}
            target="_blank"
            rel="noopener noreferrer"
            className="btn-outline"
          >
            {t('marketplace.visitWebsite')}
          </a>
        )}

        <button
          className="btn-danger delete-btn"
          onClick={handleDeleteClick}
        >
          {t('marketplace.myIntegrations.remove')}
        </button>
      </div>
    </div>
  );
};

export default UserIntegrationCard; 